import React, { useState } from 'react'
import { trackContactFormSubmit } from '../lib/analytics'

interface ContactFormData {
  firstName: string
  lastName: string
  email: string
  company: string
  service: string
  message: string
  newsletter: boolean
  website: string
  formLoadTime: number
}

const LEAD_API_URL = import.meta.env.VITE_LEAD_API_URL || ''

const ContactForm = () => {
  const [formData, setFormData] = useState<ContactFormData>({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    service: '',
    message: '',
    newsletter: false,
    website: '',
    formLoadTime: Date.now(),
  })
  const [isLoading, setIsLoading] = useState(false)
  const [status, setStatus] = useState<'idle' | 'success' | 'success-mailto' | 'error'>('idle')

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      if (LEAD_API_URL) {
        // Submit to API
        const response = await fetch(`${LEAD_API_URL}/leads`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            type: 'contact',
            ...formData,
            source: 'website-contact-form',
          }),
        })

        if (!response.ok) {
          throw new Error('Failed to submit form')
        }

        setStatus('success')
        trackContactFormSubmit()

        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          company: '',
          service: '',
          message: '',
          newsletter: false,
          website: '',
          formLoadTime: Date.now(),
        })
      } else {
        // Fallback to mailto if API not configured
        const subject = `New Inquiry: ${formData.service || 'General'} - ${formData.firstName} ${formData.lastName}`
        const body = [
          `Name: ${formData.firstName} ${formData.lastName}`,
          `Email: ${formData.email}`,
          formData.company ? `Company: ${formData.company}` : '',
          formData.service ? `Service Interest: ${formData.service}` : '',
          `Newsletter Signup: ${formData.newsletter ? 'Yes' : 'No'}`,
          '',
          'Message:',
          formData.message,
        ].filter(line => line !== null).join('\n')

        window.location.href = `mailto:info@demirix.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
        setStatus('success-mailto')
        trackContactFormSubmit()
      }
    } catch (error) {
      setStatus('error')
    } finally {
      setIsLoading(false)
      setTimeout(() => setStatus('idle'), 5000)
    }
  }

  return (
    <div>
      <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">Get in Touch</h2>
      
      {status === 'success' && (
        <div className="mb-6 p-4 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-lg">
          <h3 className="font-semibold mb-2">Thank you for your inquiry!</h3>
          <p>We've received your message and will get back to you within 24 hours.</p>
        </div>
      )}

      {status === 'success-mailto' && (
        <div className="mb-6 p-4 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-lg">
          <h3 className="font-semibold mb-2">Thank you for your inquiry!</h3>
          <p>Your email client should open with your message. Please send the email to complete your inquiry.</p>
        </div>
      )}

      {status === 'error' && (
        <div className="mb-6 p-4 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 rounded-lg">
          <p>Something went wrong. Please try again or email us directly at <a href="mailto:info@demirix.com" className="underline">info@demirix.com</a></p>
        </div>
      )}
      
      <form className="space-y-6" onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              First Name *
            </label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              required
              disabled={isLoading}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200 dark:bg-gray-700 dark:text-white"
              placeholder="Your first name"
            />
          </div>
          <div>
            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Last Name *
            </label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              required
              disabled={isLoading}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200 dark:bg-gray-700 dark:text-white"
              placeholder="Your last name"
            />
          </div>
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Email Address *
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            disabled={isLoading}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200 dark:bg-gray-700 dark:text-white"
            placeholder="your.email@example.com"
          />
        </div>

        <div>
          <label htmlFor="company" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Company (Optional)
          </label>
          <input
            type="text"
            id="company"
            name="company"
            value={formData.company}
            onChange={handleChange}
            disabled={isLoading}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200 dark:bg-gray-700 dark:text-white"
            placeholder="Your company name"
          />
        </div>

        <div>
          <label htmlFor="service" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Service Interest
          </label>
          <div className="relative">
            <select
              id="service"
              name="service"
              value={formData.service}
              onChange={handleChange}
              disabled={isLoading}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200 dark:bg-gray-700 dark:text-white appearance-none bg-white dark:bg-gray-700 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                backgroundPosition: 'right 0.75rem center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: '1.5em 1.5em',
                paddingRight: '2.5rem'
              }}
            >
              <option value="">Select a service</option>
              <option value="web-development">Web Development</option>
              <option value="cloud-solutions">Cloud Solutions</option>
              <option value="it-consulting">IT Consulting</option>
              <option value="mobile-development">Mobile Development</option>
              <option value="e-commerce">E-commerce Solutions</option>
              <option value="support">Support & Maintenance</option>
              <option value="ai">AI Solutions</option>
              <option value="web3">Web3 & Blockchain</option>
              <option value="other">Other / Custom Solution</option>
            </select>
          </div>
        </div>

        <div>
          <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Project Details *
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            rows={6}
            required
            disabled={isLoading}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200 dark:bg-gray-700 dark:text-white"
            placeholder="Tell us about your project, goals, timeline, and any specific requirements..."
          />
        </div>


        {/* Honeypot field - hidden from humans, bots fill it */}
        <input
          type="text"
          name="website"
          value={formData.website}
          onChange={handleChange}
          style={{ position: 'absolute', left: '-9999px' }}
          tabIndex={-1}
          autoComplete="off"
        />

        <button
          type="submit"
          disabled={isLoading}
          className="w-full btn-primary text-lg py-4 disabled:opacity-50"
        >
          {isLoading ? 'Sending...' : 'Send Message'}
        </button>
      </form>
    </div>
  )
}

export default ContactForm